import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Phone, History, BookOpen, MessageSquare, Settings,
  Sun, Moon, Menu, X, Zap, BarChart3, Users, Building2,
  User, LogOut, Shield, ChevronDown, UserCheck, Bell
} from 'lucide-react';

const getNavItems = (role) => {
  const items = [
    { path: '/call', label: 'Llamada', icon: Phone },
    { path: '/dashboard', label: 'Panel de Control', icon: BarChart3 },
    { path: '/sessions', label: 'Historial', icon: History },
  ];

  if (role === 'ADMIN' || role === 'COMPANY_ADMIN') {
    items.push({ path: '/analytics/agents', label: 'Analitica Agentes', icon: UserCheck });
    items.push({ path: '/admin/users', label: 'Usuarios', icon: Users });
  }
  if (role === 'ADMIN') {
    items.push({ path: '/admin/companies', label: 'Empresas', icon: Building2 });
  }

  items.push(
    { path: '/admin/knowledge', label: 'Base de Conocimiento', icon: BookOpen },
    { path: '/admin/prompts', label: 'Prompts', icon: MessageSquare },
    { path: '/notifications', label: 'Notificaciones', icon: Bell },
    { path: '/admin/settings', label: 'Configuracion', icon: Settings },
  );

  return items;
};

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState(() => document.documentElement.getAttribute('data-theme') || 'dark');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, logout, isImpersonating } = useAuth();
  const navigate = useNavigate();

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('theme', next);
    setTheme(next);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = getNavItems(user?.role);
  const roleLabel = { ADMIN: 'Admin', COMPANY_ADMIN: 'Admin Empresa', USER: 'Usuario' };

  return (
    <div className="flex h-screen" style={{ backgroundColor: 'var(--bg-primary)', paddingTop: isImpersonating ? '36px' : 0 }}>
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-[240px] flex flex-col transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        style={{ backgroundColor: 'var(--surface-primary)', borderRight: '1px solid var(--border-color)', top: isImpersonating ? '36px' : 0 }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2.5 px-5 py-5 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--accent-primary)' }}>
            <Zap size={17} className="text-white" />
          </div>
          <div>
            <span className="text-sm font-heading font-bold" style={{ color: 'var(--text-primary)' }}>SalesAI</span>
            <span className="text-[9px] font-mono ml-1" style={{ color: 'var(--text-muted)' }}>COPILOT</span>
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navItems.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                  isActive ? 'shadow-sm' : 'hover:translate-x-0.5'
                }`
              }
              style={({ isActive }) => ({
                backgroundColor: isActive ? 'var(--accent-primary)' : 'transparent',
                color: isActive ? '#ffffff' : 'var(--text-secondary)',
              })}
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="border-t px-3 py-3" style={{ borderColor: 'var(--border-color)' }}>
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              data-testid="user-menu-btn"
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors hover:bg-white/5"
            >
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{user?.first_name} {user?.last_name}</p>
                <p className="text-[9px] truncate" style={{ color: 'var(--text-muted)' }}>{roleLabel[user?.role]}</p>
              </div>
              <ChevronDown size={12} style={{ color: 'var(--text-muted)' }} />
            </button>

            {showUserMenu && (
              <div className="absolute bottom-full left-0 right-0 mb-1 rounded-lg border shadow-lg py-1" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
                <button onClick={() => { navigate('/profile'); setShowUserMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors hover:bg-white/5"
                  style={{ color: 'var(--text-secondary)' }} data-testid="nav-profile">
                  <User size={13} /> Mi Perfil
                </button>
                <button onClick={toggleTheme}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors hover:bg-white/5"
                  style={{ color: 'var(--text-secondary)' }}>
                  {theme === 'dark' ? <Sun size={13} /> : <Moon size={13} />}
                  {theme === 'dark' ? 'Modo Claro' : 'Modo Oscuro'}
                </button>
                <div className="border-t my-1" style={{ borderColor: 'var(--border-color)' }} />
                <button onClick={handleLogout} data-testid="logout-btn"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors hover:bg-red-500/10 text-red-400">
                  <LogOut size={13} /> Cerrar Sesion
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <button onClick={() => setSidebarOpen(true)} style={{ color: 'var(--text-primary)' }}>
            <Menu size={22} />
          </button>
          <span className="text-sm font-heading font-bold" style={{ color: 'var(--text-primary)' }}>SalesAI</span>
          <div style={{ width: 22 }} />
        </div>
        {children}
      </main>
    </div>
  );
}
